/**
 * InstallDetailsAdmin — INSTALL-SPEC-ADMIN (Seth 2026-09-25, "Proceed with the
 * ideas"): Settings → Install Details. The questions on an install job's
 * Install Details — the office card, the printed / emailed work order and the
 * crew's phone all read this one list (useInstallBank). Reword a question, add
 * or rename its choices, say which equipment asks it, add questions and
 * sections, archive — no code edits (the admin-self-service rule).
 *
 * Competitor frame: ServiceTitan's forms (conditional questions, triggered by
 * job type / equipment) and Jobber's job forms (attached by line item) —
 * adopted: questions tailored by what's on the job, one shared list. Rejected:
 * a free-form builder with every field type — an install needs chips, "how
 * much" and a detail line, and every extra type is something the phone and the
 * printed work order would have to learn to show.
 *
 * Safety (installBank.ts): keys never change, archive never deletes, and
 * renaming a choice carries the new wording onto open jobs that picked it.
 */
import { useMemo, useState, type ReactNode } from "react";
import {
  AlertTriangle, ArrowDown, ArrowUp, Check, ChevronDown, Lock, Pencil, Plus, RotateCcw, Search, Sparkles, X,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { useEquipmentCategories } from "./equipmentCategories";
import { ALL_TAGS, ITEM_KEY_LABEL, TAG_LABEL, type QuestionRow } from "@/features/jobs/installSpec";
import {
  slugKey, useInstallAnswerCounts, useInstallBankAdmin, useMoveInstallQuestion, useMoveInstallSection,
  useSaveInstallQuestion, useSaveInstallSection, useSetQuestionActive, type BankAdmin, type SectionAdminRow,
} from "@/features/jobs/installBank";

// ── What a question can be asked for ────────────────────────────────────────
interface Opt { value: string; label: string }
interface Vocab {
  equipment: Opt[];
  addons: Opt[];
  labelOf: (v: string) => string;
  isBuiltIn: (v: string) => boolean;
}

/** Equipment the built-in groups already cover (a rooftop is a "Packaged Unit"). */
const COVERED_BY_TAG = new Set([
  "furnace", "ac", "heat-pump", "coil", "air-handler", "mini-split", "boiler", "water-heater",
  "garage-heater", "magic-pak", "rooftop", "other",
]);

function useVocab(): Vocab {
  const { data: cats } = useEquipmentCategories();
  return useMemo(() => {
    const active = (cats ?? []).filter((c) => c.active);
    const equipment: Opt[] = [
      ...ALL_TAGS.map((t) => ({ value: t as string, label: TAG_LABEL[t] })),
      // A kind of equipment added in Settings (Pool Heater…) can ask questions too.
      ...active.filter((c) => c.grouping === "system" && !COVERED_BY_TAG.has(c.key)).map((c) => ({ value: c.key, label: c.label })),
    ];
    const addonMap = new Map<string, string>(Object.entries(ITEM_KEY_LABEL));
    for (const c of active) if (c.grouping === "auxiliary") addonMap.set(c.key, c.label);
    const addons = [...addonMap].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
    const all = new Map<string, string>([...equipment, ...addons].map((o) => [o.value, o.label]));
    for (const c of cats ?? []) if (!all.has(c.key)) all.set(c.key, c.label);
    return {
      equipment,
      addons,
      labelOf: (v) => all.get(v) ?? v,
      isBuiltIn: (v) => (ALL_TAGS as string[]).includes(v),
    };
  }, [cats]);
}

const orList = (xs: string[]) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} or ${xs[xs.length - 1]}`);

/** "Asks when the job has: 90%+ Furnace · only after “PVC Pipe” is New (80→90)" */
function askSummary(q: QuestionRow, vocab: Vocab, labelOfQ: (k: string) => string): string {
  const parts = [q.when_tags.length ? `Asks when the job has: ${orList(q.when_tags.map(vocab.labelOf))}` : "Asks on every install"];
  if (q.unless_tags.length) parts.push(`but not: ${orList(q.unless_tags.map(vocab.labelOf))}`);
  if (q.when_answer) parts.push(`only after “${labelOfQ(q.when_answer.key)}” is ${orList(q.when_answer.in)}`);
  return parts.join(" · ");
}

function fillSummary(q: QuestionRow, vocab: Vocab): string {
  return (q.implied ?? []).map((i) => `Pre-picks “${i.pick}” when the job has: ${vocab.labelOf(i.equip ?? i.tag ?? "")}`).join(" · ");
}

const inputCls =
  "h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 placeholder:text-slate-400 " +
  "focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25 disabled:bg-slate-50 disabled:text-slate-500";

function IconBtn({ label, onClick, disabled, children, danger }: { label: string; onClick: () => void; disabled?: boolean; children: ReactNode; danger?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={label} aria-label={label}
      className={cn(
        "rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
        danger ? "hover:text-rose-600" : "hover:text-slate-700",
      )}>
      {children}
    </button>
  );
}

function ConfirmBar({ text, yes, busy, onYes, onNo }: { text: string; yes: string; busy?: boolean; onYes: () => void; onNo: () => void }) {
  return (
    <div role="alert" className="mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-2">
      <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
      <p className="min-w-0 flex-1 text-sm text-amber-900">{text}</p>
      <Button size="sm" onClick={onYes} loading={busy}>{yes}</Button>
      <Button size="sm" variant="ghost" onClick={onNo}>Cancel</Button>
    </div>
  );
}

// ── The page ────────────────────────────────────────────────────────────────
export function InstallDetailsAdmin() {
  const { data, isLoading, error } = useInstallBankAdmin();
  const { data: counts } = useInstallAnswerCounts();
  const vocab = useVocab();
  const saveSec = useSaveInstallSection();
  const moveSec = useMoveInstallSection();
  const moveQ = useMoveInstallQuestion();
  const setActive = useSetQuestionActive();
  const [find, setFind] = useState("");
  /** A question key, or `new:<section key>`. */
  const [editing, setEditing] = useState<string | null>(null);
  const [addingSection, setAddingSection] = useState(false);
  const [newSection, setNewSection] = useState("");
  const [renaming, setRenaming] = useState<{ key: string; title: string } | null>(null);
  const [confirm, setConfirm] = useState<{ kind: "section" | "question"; key: string } | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const say = (msg: string) => {
    setFlash(msg);
    window.setTimeout(() => setFlash((m) => (m === msg ? null : m)), 6000);
  };

  if (isLoading) {
    return <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-100" />)}</div>;
  }
  if (error || !data) {
    return <Card className="p-4 text-sm text-rose-700">Couldn&apos;t load the install questions — refresh to try again.</Card>;
  }

  const bank: BankAdmin = data;
  const sections = bank.sections.filter((s) => s.active);
  const archivedSections = bank.sections.filter((s) => !s.active);
  const qs = bank.questions;
  const labelOfQ = (k: string) => qs.find((q) => q.key === k)?.label ?? k;
  const inSection = (key: string) => qs.filter((q) => q.active && q.section_key === key);
  const archivedQs = qs.filter((q) => !q.active);
  const needle = find.trim().toLowerCase();
  const matches = (q: QuestionRow) => !needle
    || q.label.toLowerCase().includes(needle)
    || q.options.some((o) => o.toLowerCase().includes(needle))
    || (q.hint ?? "").toLowerCase().includes(needle);
  const dependentsOf = (key: string) => qs.filter((q) => q.active && q.when_answer?.key === key);
  const sectionTitle = (key: string) => bank.sections.find((s) => s.key === key)?.title ?? key;
  const busy = saveSec.isPending || moveSec.isPending || moveQ.isPending || setActive.isPending;

  const archiveQuestion = (q: QuestionRow) => {
    setActive.mutate({ key: q.key, active: false }, {
      onSuccess: () => { setConfirm(null); say(`Archived “${q.label}” — Restore is at the bottom of the page.`); },
    });
  };

  const addSection = () => {
    const title = newSection.trim();
    if (!title) return;
    const max = Math.max(0, ...bank.sections.map((s) => s.sort_order));
    saveSec.mutate({ title, taken: new Set(bank.sections.map((s) => s.key)), sort_order: max + 10 }, {
      onSuccess: () => { setNewSection(""); setAddingSection(false); say(`Added the “${title}” section — add its first question below.`); },
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative min-w-[220px] max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Find a question or choice"
            aria-label="Find a question or choice" className={cn(inputCls, "pl-9")} />
        </label>
        <InfoTip label="How the installer guide works">
          One list for the job&apos;s Installer Guide, the sales tech&apos;s install packet, the printed work order and the crew&apos;s
          phone. Answers are saved under the question, so rewording, moving or re-targeting one never loses them, and renaming a
          choice updates open jobs that picked it.
        </InfoTip>
        <Button size="sm" variant="secondary" className="ml-auto" onClick={() => setAddingSection((v) => !v)}>
          <Plus className="h-3.5 w-3.5" /> Add a Section
        </Button>
      </div>

      {flash && (
        <div role="status" className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">
          <Check className="h-4 w-4 shrink-0" strokeWidth={3} /> {flash}
        </div>
      )}
      {(saveSec.error || moveSec.error || moveQ.error || setActive.error) && (
        <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          That didn&apos;t save — {((saveSec.error || moveSec.error || moveQ.error || setActive.error) as Error).message}
        </div>
      )}

      {addingSection && (
        <Card className="p-3">
          <form onSubmit={(e) => { e.preventDefault(); addSection(); }} className="flex flex-wrap items-center gap-2">
            <input autoFocus value={newSection} onChange={(e) => setNewSection(e.target.value)} placeholder="e.g. Generator"
              aria-label="New section name" className={cn(inputCls, "min-w-[220px] flex-1")} />
            <Button size="sm" type="submit" disabled={!newSection.trim()} loading={saveSec.isPending}>Add Section</Button>
            <Button size="sm" variant="ghost" type="button" onClick={() => { setAddingSection(false); setNewSection(""); }}>Cancel</Button>
          </form>
        </Card>
      )}

      {sections.map((s, si) => {
        const list = inSection(s.key);
        const shown = list.filter(matches);
        if (needle && shown.length === 0) return null;
        const addingHere = editing === `new:${s.key}`;
        return (
          <Card key={s.key} className="overflow-hidden">
            <div className="group/sec border-b border-slate-100 bg-slate-50/50 px-4 py-2.5">
              {renaming?.key === s.key ? (
                <form className="flex flex-wrap items-center gap-2"
                  onSubmit={(e) => { e.preventDefault(); saveSec.mutate({ key: s.key, title: renaming.title }, { onSuccess: () => setRenaming(null) }); }}>
                  <input autoFocus value={renaming.title} onChange={(e) => setRenaming({ key: s.key, title: e.target.value })}
                    aria-label="Section name" className={cn(inputCls, "min-w-[220px] flex-1")} />
                  <Button size="sm" type="submit" disabled={!renaming.title.trim()} loading={saveSec.isPending}>Save</Button>
                  <Button size="sm" variant="ghost" type="button" onClick={() => setRenaming(null)}>Cancel</Button>
                </form>
              ) : (
                <div className="flex items-center gap-2">
                  <h4 className="min-w-0 flex-1 truncate text-base font-bold text-slate-900">{s.title}</h4>
                  <span className="shrink-0 text-xs text-slate-500">{list.length} question{list.length === 1 ? "" : "s"}</span>
                  <div className="flex shrink-0 items-center opacity-60 transition-opacity group-hover/sec:opacity-100 focus-within:opacity-100">
                    <IconBtn label="Move section up" disabled={si === 0 || busy} onClick={() => moveSec.mutate({ list: sections, key: s.key, dir: -1 })}><ArrowUp className="h-4 w-4" /></IconBtn>
                    <IconBtn label="Move section down" disabled={si === sections.length - 1 || busy} onClick={() => moveSec.mutate({ list: sections, key: s.key, dir: 1 })}><ArrowDown className="h-4 w-4" /></IconBtn>
                    <IconBtn label={`Rename ${s.title}`} onClick={() => setRenaming({ key: s.key, title: s.title })}><Pencil className="h-3.5 w-3.5" /></IconBtn>
                    <IconBtn label={`Archive ${s.title}`} danger disabled={list.some((q) => q.locked)}
                      onClick={() => (list.length ? setConfirm({ kind: "section", key: s.key }) : saveSec.mutate({ key: s.key, title: s.title, active: false }))}>
                      <X className="h-4 w-4" />
                    </IconBtn>
                  </div>
                </div>
              )}
              {confirm?.kind === "section" && confirm.key === s.key && (
                <ConfirmBar
                  text={`Archive “${s.title}”? Its ${list.length} question${list.length === 1 ? "" : "s"} stop being asked on every job. Answers stay on the jobs, and Restore brings it all back.`}
                  yes="Archive It" busy={saveSec.isPending}
                  onYes={() => saveSec.mutate({ key: s.key, title: s.title, active: false }, { onSuccess: () => { setConfirm(null); say(`Archived the “${s.title}” section.`); } })}
                  onNo={() => setConfirm(null)} />
              )}
            </div>

            <ol className="divide-y divide-slate-100">
              {shown.map((q) => {
                const i = list.findIndex((x) => x.key === q.key);
                if (editing === q.key) {
                  return (
                    <li key={q.key} className="p-3">
                      <QuestionEditor q={q} sectionKey={s.key} bank={bank} vocab={vocab} count={counts?.[q.key] ?? 0}
                        onClose={(msg) => { setEditing(null); if (msg) say(msg); }} />
                    </li>
                  );
                }
                const count = counts?.[q.key] ?? 0;
                const deps = dependentsOf(q.key);
                const fill = fillSummary(q, vocab);
                return (
                  <li key={q.key} className="group px-4 py-3">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 w-5 shrink-0 text-right text-xs font-semibold tabular-nums text-slate-300">{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <button type="button" onClick={() => setEditing(q.key)} className="text-left text-sm font-semibold text-slate-900 hover:text-brand-800">
                            {q.label}
                          </button>
                          {q.locked && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800" title="Its choices decide the venting questions, so they're fixed">
                              <Lock className="h-3 w-3" /> Sets the venting questions
                            </span>
                          )}
                          {q.optional && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">Optional</span>}
                          {q.is_text && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">Typed answer</span>}
                        </div>
                        {!q.is_text && q.options.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {q.options.map((o) => (
                              <span key={o} className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-slate-700 ring-1 ring-inset ring-slate-200">
                                {o}{q.amount_for.includes(o) && <span className="font-normal text-slate-500"> · how many{q.unit ? ` ${q.unit}` : ""}</span>}
                              </span>
                            ))}
                            {q.note && <span className="rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-[11px] text-slate-500">+ detail</span>}
                          </div>
                        )}
                        <p className="mt-1.5 text-xs text-slate-500">{askSummary(q, vocab, labelOfQ)}</p>
                        {fill && (
                          <p className="mt-0.5 flex items-start gap-1 text-xs text-slate-500">
                            <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-brand-500" aria-hidden /> {fill}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-0.5">
                        {count > 0 && <span className="mr-1.5 whitespace-nowrap text-xs text-slate-500" title="Open jobs with an answer to this question">{count} open job{count === 1 ? "" : "s"}</span>}
                        <div className="flex items-center opacity-50 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                          <IconBtn label="Move up" disabled={i === 0 || busy} onClick={() => moveQ.mutate({ list, key: q.key, dir: -1 })}><ArrowUp className="h-4 w-4" /></IconBtn>
                          <IconBtn label="Move down" disabled={i === list.length - 1 || busy} onClick={() => moveQ.mutate({ list, key: q.key, dir: 1 })}><ArrowDown className="h-4 w-4" /></IconBtn>
                          <IconBtn label={`Edit ${q.label}`} onClick={() => setEditing(q.key)}><Pencil className="h-3.5 w-3.5" /></IconBtn>
                          <IconBtn label={q.locked ? "Can't archive — it decides the venting questions" : `Archive ${q.label}`} danger disabled={q.locked || busy}
                            onClick={() => (count > 0 || deps.length > 0 ? setConfirm({ kind: "question", key: q.key }) : archiveQuestion(q))}>
                            <X className="h-4 w-4" />
                          </IconBtn>
                        </div>
                      </div>
                    </div>
                    {confirm?.kind === "question" && confirm.key === q.key && (
                      <ConfirmBar
                        text={[
                          `Archive “${q.label}”?`,
                          count > 0 ? `${count} open job${count === 1 ? " has an answer" : "s have answers"} — kept on the job${count === 1 ? "" : "s"}, but hidden from the card and the work order until you restore it.` : "",
                          deps.length ? `${orList(deps.map((d) => `“${d.label}”`))} only ask${deps.length === 1 ? "s" : ""} after this answer, so ${deps.length === 1 ? "it" : "they"} won't be asked on new jobs.` : "",
                        ].filter(Boolean).join(" ")}
                        yes="Archive It" busy={setActive.isPending}
                        onYes={() => archiveQuestion(q)} onNo={() => setConfirm(null)} />
                    )}
                  </li>
                );
              })}
              {addingHere && (
                <li className="p-3">
                  <QuestionEditor q={null} sectionKey={s.key} bank={bank} vocab={vocab} count={0}
                    onClose={(msg) => { setEditing(null); if (msg) say(msg); }} />
                </li>
              )}
            </ol>
            {!needle && !addingHere && (
              <div className={cn("px-4 py-2", (shown.length > 0) && "border-t border-slate-100")}>
                <button type="button" onClick={() => setEditing(`new:${s.key}`)}
                  className="inline-flex items-center gap-1 rounded-lg px-1 py-1 text-sm font-medium text-brand-700 transition-colors hover:text-brand-900">
                  <Plus className="h-4 w-4" /> Add a question
                </button>
              </div>
            )}
          </Card>
        );
      })}

      {needle && sections.every((s) => inSection(s.key).filter(matches).length === 0) && (
        <Card className="p-6 text-center text-sm text-slate-500">No question or choice matches “{find.trim()}”.</Card>
      )}

      {!needle && (archivedSections.length > 0 || archivedQs.length > 0) && (
        <Card className="px-4 py-3">
          <h4 className="text-sm font-bold text-slate-700">Archived</h4>
          <p className="mt-0.5 text-xs text-slate-500">Not asked on any job. Answers already saved stay on the jobs — Restore brings them back.</p>
          <ul className="mt-2 divide-y divide-slate-100">
            {archivedSections.map((s: SectionAdminRow) => (
              <li key={`s:${s.key}`} className="flex items-center gap-3 py-2">
                <span className="min-w-0 flex-1 truncate text-sm text-slate-600">{s.title} <span className="text-xs text-slate-400">· section</span></span>
                <Button size="sm" variant="secondary" disabled={busy} onClick={() => saveSec.mutate({ key: s.key, title: s.title, active: true }, { onSuccess: () => say(`Restored the “${s.title}” section.`) })}>
                  <RotateCcw className="h-3.5 w-3.5" /> Restore
                </Button>
              </li>
            ))}
            {archivedQs.map((q) => {
              const secOff = !sections.some((s) => s.key === q.section_key);
              return (
                <li key={`q:${q.key}`} className="flex items-center gap-3 py-2">
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-600">
                    {q.label} <span className="text-xs text-slate-400">· {sectionTitle(q.section_key)}{secOff ? " (archived section)" : ""}</span>
                  </span>
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => setActive.mutate({ key: q.key, active: true }, { onSuccess: () => say(`Restored “${q.label}”.`) })}>
                    <RotateCcw className="h-3.5 w-3.5" /> Restore
                  </Button>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}

// ── The editor ──────────────────────────────────────────────────────────────
interface ChoiceDraft { id: string; text: string; orig?: string; amount: boolean }
interface ImpliedDraft { id: string; src: string; choiceId: string }
interface Draft {
  label: string;
  section_key: string;
  is_text: boolean;
  choices: ChoiceDraft[];
  unit: string;
  detail: boolean;
  note: string;
  hint: string;
  hint_when: string[];
  when_tags: string[];
  unless_tags: string[];
  wa_key: string;
  wa_in: string[];
  implied: ImpliedDraft[];
  optional: boolean;
}

let uid = 0;
const nid = () => `c${++uid}`;

function toDraft(q: QuestionRow | null, sectionKey: string, sectionFirst: QuestionRow | undefined): Draft {
  if (!q) {
    return {
      label: "", section_key: sectionKey, is_text: false, choices: [{ id: nid(), text: "", amount: false }],
      unit: "", detail: false, note: "", hint: "", hint_when: [],
      // A new question in the Furnace section asks for furnaces, like its neighbors (change it below).
      when_tags: sectionFirst?.when_tags ?? [], unless_tags: [], wa_key: "", wa_in: [], implied: [], optional: false,
    };
  }
  const choices = q.options.map((o) => ({ id: nid(), text: o, orig: o, amount: q.amount_for.includes(o) }));
  return {
    label: q.label,
    section_key: q.section_key,
    is_text: q.is_text,
    choices,
    unit: q.unit ?? "",
    detail: !q.is_text && !!q.note,
    note: q.note ?? "",
    hint: q.hint ?? "",
    hint_when: q.hint_when ?? [],
    when_tags: q.when_tags ?? [],
    unless_tags: q.unless_tags ?? [],
    wa_key: q.when_answer?.key ?? "",
    wa_in: q.when_answer?.in ?? [],
    implied: (q.implied ?? []).map((i) => ({ id: nid(), src: i.equip ?? i.tag ?? "", choiceId: choices.find((c) => c.text === i.pick)?.id ?? "" })),
    optional: q.optional,
  };
}

function Chip({ on, onClick, disabled, children }: { on: boolean; onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick} disabled={disabled}
      className={cn(
        "rounded-full px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
        on ? "bg-brand-600 text-white hover:bg-brand-700" : "bg-white text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-50",
      )}>
      {children}
    </button>
  );
}

function TagPicker({ value, onChange, vocab, label }: { value: string[]; onChange: (v: string[]) => void; vocab: Vocab; label: string }) {
  const toggle = (v: string) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const known = new Set([...vocab.equipment, ...vocab.addons].map((o) => o.value));
  const unknown = value.filter((v) => !known.has(v));
  return (
    <div role="group" aria-label={label} className="space-y-2">
      {[{ title: "Equipment", opts: vocab.equipment }, { title: "Add-ons & parts", opts: vocab.addons }].map((g) => (
        <div key={g.title}>
          <div className="mb-1 text-xs font-medium text-slate-500">{g.title}</div>
          <div className="flex flex-wrap gap-1.5">
            {g.opts.map((o) => <Chip key={o.value} on={value.includes(o.value)} onClick={() => toggle(o.value)}>{o.label}</Chip>)}
          </div>
        </div>
      ))}
      {unknown.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {unknown.map((v) => <Chip key={v} on onClick={() => toggle(v)}>{vocab.labelOf(v)} (archived)</Chip>)}
        </div>
      )}
    </div>
  );
}

function Field({ label, help, info, children }: { label: string; help?: ReactNode; info?: ReactNode; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-slate-800">{label}{info && <InfoTip label={`About ${label}`}>{info}</InfoTip>}</div>
      {children}
      {help && <p className="mt-1 text-xs text-slate-500">{help}</p>}
    </div>
  );
}

function QuestionEditor({ q, sectionKey, bank, vocab, count, onClose }: {
  q: QuestionRow | null;
  sectionKey: string;
  bank: BankAdmin;
  vocab: Vocab;
  count: number;
  onClose: (msg?: string) => void;
}) {
  const sectionQs = bank.questions.filter((x) => x.active && x.section_key === sectionKey);
  const [d, setD] = useState<Draft>(() => toDraft(q, sectionKey, sectionQs[0]));
  const [err, setErr] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [more, setMore] = useState(() => !!q && (q.unless_tags.length > 0 || !!q.when_answer || (q.implied ?? []).length > 0 || q.hint_when.length > 0));
  const save = useSaveInstallQuestion();
  const locked = !!q?.locked;
  const set = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));
  const sections = bank.sections.filter((s) => s.active);
  const secTitle = (k: string) => bank.sections.find((s) => s.key === k)?.title ?? k;

  const others = bank.questions.filter((x) => x.active && x.key !== q?.key && !x.is_text && x.options.length > 0);
  const waQ = bank.questions.find((x) => x.key === d.wa_key);
  const removed = q ? q.options.filter((o) => !d.choices.some((c) => c.orig === o)) : [];
  const renamed = d.choices.filter((c) => c.orig && c.text.trim() && c.orig !== c.text.trim());
  const deps = q ? bank.questions.filter((x) => x.active && x.when_answer?.key === q.key && x.when_answer.in.some((v) => removed.includes(v))) : [];
  const anyAmount = d.choices.some((c) => c.amount);

  const setChoice = (id: string, patch: Partial<ChoiceDraft>) => set({ choices: d.choices.map((c) => (c.id === id ? { ...c, ...patch } : c)) });
  const addChoice = (at = d.choices.length) => {
    const c = { id: nid(), text: "", amount: false };
    const next = [...d.choices];
    next.splice(at, 0, c);
    set({ choices: next });
    setFocusId(c.id);
  };
  const moveChoice = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= d.choices.length) return;
    const next = [...d.choices];
    [next[i], next[j]] = [next[j], next[i]];
    set({ choices: next });
  };
  const removeChoice = (id: string) => set({
    choices: d.choices.filter((c) => c.id !== id),
    implied: d.implied.filter((i) => i.choiceId !== id),
  });
  const toggleAmount = (c: ChoiceDraft) => set({
    choices: d.choices.map((x) => (x.id === c.id ? { ...x, amount: !x.amount } : x)),
    unit: !c.amount && !d.unit.trim() ? "ft" : d.unit,
  });

  const onSave = () => {
    setErr(null);
    const label = d.label.trim();
    if (!label) return setErr("Give the question a name.");
    let choices = d.choices.map((c) => ({ ...c, text: c.text.trim() }));
    if (!d.is_text) {
      const blankOld = choices.find((c) => !c.text && c.orig);
      if (blankOld) return setErr(`The choice “${blankOld.orig}” is blank — type its new wording, or remove it.`);
      choices = choices.filter((c) => c.text);
      if (choices.length === 0) return setErr("Add at least one choice — or make it a typed answer.");
      const seen = new Set<string>();
      for (const c of choices) {
        const k = c.text.toLowerCase();
        if (seen.has(k)) return setErr(`Two choices say “${c.text}”.`);
        seen.add(k);
      }
    }
    const waIn = waQ ? d.wa_in.filter((v) => waQ.options.includes(v)) : [];
    if (d.wa_key && waIn.length === 0) return setErr("Pick which answer it waits for — or set “Only ask after” back to No.");

    const isNew = !q;
    const movedSection = !isNew && q.section_key !== d.section_key;
    const endOf = (sec: string) => Math.max(0, ...bank.questions.filter((x) => x.section_key === sec).map((x) => x.sort_order)) + 10;
    const key = q?.key ?? slugKey(label, new Set(bank.questions.map((x) => x.key)));
    const implied = d.is_text ? [] : d.implied.flatMap((i) => {
      const c = choices.find((x) => x.id === i.choiceId);
      if (!i.src || !c) return [];
      return [vocab.isBuiltIn(i.src) ? { tag: i.src, pick: c.text } : { equip: i.src, pick: c.text }];
    });
    const hint = d.hint.trim();
    const row = {
      key,
      section_key: d.section_key,
      label,
      options: d.is_text ? [] : choices.map((c) => c.text),
      amount_for: d.is_text ? [] : choices.filter((c) => c.amount).map((c) => c.text),
      unit: !d.is_text && choices.some((c) => c.amount) ? (d.unit.trim() || null) : null,
      note: d.is_text ? (d.note.trim() || null) : d.detail ? (d.note.trim() || "detail…") : null,
      hint: hint || null,
      hint_when: hint ? d.hint_when : [],
      is_text: d.is_text,
      when_tags: d.when_tags,
      unless_tags: d.unless_tags,
      when_answer: d.wa_key ? { key: d.wa_key, in: waIn } : null,
      implied,
      optional: d.optional,
      locked,
      ...(isNew || movedSection ? { sort_order: endOf(d.section_key) } : {}),
    };
    const renames = d.is_text ? [] : choices.filter((c) => c.orig && c.orig !== c.text).map((c) => ({ from: c.orig as string, to: c.text }));
    save.mutate({ row, isNew, renames }, {
      onSuccess: (r) => onClose(
        isNew ? `Added “${label}” to ${secTitle(d.section_key)}.`
          : r.jobsRenamed > 0 ? `Saved — ${r.jobsRenamed} open job${r.jobsRenamed === 1 ? "" : "s"} had the old wording and now show${r.jobsRenamed === 1 ? "s" : ""} the new one.`
            : `Saved “${label}”.`,
      ),
      onError: (e) => setErr(`That didn't save — ${(e as Error).message}`),
    });
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(); }}
      className="space-y-5 rounded-2xl border border-brand-200 bg-gradient-to-b from-brand-50/40 to-white p-4 shadow-sm">
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_240px]">
        <label className="block">
          <span className="mb-1.5 block text-sm font-semibold text-slate-800">Question</span>
          <input autoFocus={!q} value={d.label} onChange={(e) => set({ label: e.target.value })} placeholder="e.g. Return-Air Drop" className={inputCls} />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-semibold text-slate-800">Section</span>
          <select value={d.section_key} onChange={(e) => set({ section_key: e.target.value })} className={inputCls}>
            {sections.map((s) => <option key={s.key} value={s.key}>{s.title}</option>)}
          </select>
        </label>
      </div>

      {locked && (
        <p className="flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          Its two choices decide which venting questions ask (80% → metal vent, 90%+ → PVC), so they and who it asks for are fixed.
          You can reword the question, its helper line and its section.
        </p>
      )}

      <Field label="Answer">
        <div role="radiogroup" aria-label="Answer type" className="inline-flex rounded-full bg-slate-100 p-1">
          {[{ v: false, l: "Choices" }, { v: true, l: "Typed answer" }].map((o) => (
            <button key={o.l} type="button" role="radio" aria-checked={d.is_text === o.v} disabled={locked} onClick={() => set({ is_text: o.v })}
              className={cn("rounded-full px-3.5 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed",
                d.is_text === o.v ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900")}>
              {o.l}
            </button>
          ))}
        </div>
      </Field>

      {!d.is_text ? (
        <>
          <Field label="Choices" info={locked ? undefined : "The salesperson taps one. Turn on How Much? for the choices that need a length or size typed next to them."}>
            <ol className="space-y-1.5">
              {d.choices.map((c, i) => (
                <li key={c.id}>
                  <div className="flex items-center gap-1.5">
                    <input value={c.text} disabled={locked} autoFocus={c.id === focusId} aria-label={`Choice ${i + 1}`}
                      onChange={(e) => setChoice(c.id, { text: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (!locked) addChoice(i + 1); } }}
                      placeholder="e.g. Replace Existing" className={cn(inputCls, "min-w-0 flex-1")} />
                    {!locked && (
                      <>
                        <button type="button" aria-pressed={c.amount} onClick={() => toggleAmount(c)} title="Ask how much when this is picked"
                          className={cn("inline-flex h-9 shrink-0 items-center gap-1 rounded-full px-3 text-xs font-semibold transition-colors",
                            c.amount ? "bg-brand-600 text-white hover:bg-brand-700" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50")}>
                          {c.amount && <Check className="h-3.5 w-3.5" strokeWidth={3} />} How Much?
                        </button>
                        <IconBtn label="Move choice up" disabled={i === 0} onClick={() => moveChoice(i, -1)}><ArrowUp className="h-4 w-4" /></IconBtn>
                        <IconBtn label="Move choice down" disabled={i === d.choices.length - 1} onClick={() => moveChoice(i, 1)}><ArrowDown className="h-4 w-4" /></IconBtn>
                        <IconBtn label={`Remove ${c.text || "choice"}`} danger onClick={() => removeChoice(c.id)}><X className="h-4 w-4" /></IconBtn>
                      </>
                    )}
                  </div>
                  {c.orig && c.text.trim() && c.orig !== c.text.trim() && (
                    <p className="mt-0.5 pl-1 text-xs text-slate-500">
                      Renamed from “{c.orig}”{count > 0 ? " — open jobs that picked it get the new wording" : ""}.
                    </p>
                  )}
                </li>
              ))}
            </ol>
            {!locked && (
              <button type="button" onClick={() => addChoice()} className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-900">
                <Plus className="h-4 w-4" /> Add a choice
              </button>
            )}
            {removed.length > 0 && (
              <p className="mt-2 text-xs text-slate-600">
                Removing {orList(removed.map((r) => `“${r}”`))}. Jobs that already picked {removed.length === 1 ? "it" : "one"} keep the answer — it shows as its own chip until someone picks again.
              </p>
            )}
            {deps.length > 0 && (
              <p className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                {orList(deps.map((x) => `“${x.label}”`))} only ask{deps.length === 1 ? "s" : ""} after a choice you&apos;re removing — {deps.length === 1 ? "it" : "they"} won&apos;t be asked for it any more.
              </p>
            )}
          </Field>

          {anyAmount && !locked && (
            <label className="block max-w-xs">
              <span className="mb-1.5 block text-sm font-semibold text-slate-800">How much is measured in</span>
              <input value={d.unit} onChange={(e) => set({ unit: e.target.value })} placeholder="ft, kW, zones…" className={inputCls} />
            </label>
          )}

          <Field label="Detail box">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={d.detail} onChange={(e) => set({ detail: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
              Offer a detail box after a choice is picked
            </label>
            {d.detail && (
              <input value={d.note} onChange={(e) => set({ note: e.target.value })} placeholder="Hint text in the box — e.g. route, elbows…"
                aria-label="Detail box hint text" className={cn(inputCls, "mt-2")} />
            )}
          </Field>
        </>
      ) : (
        <label className="block">
          <span className="mb-1.5 block text-sm font-semibold text-slate-800">Hint text in the box</span>
          <input value={d.note} onChange={(e) => set({ note: e.target.value })} placeholder="e.g. lockbox code, garage keypad, pets…" className={inputCls} />
        </label>
      )}

      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
        <label className="block">
          <span className="mb-1.5 block text-sm font-semibold text-slate-800">Helper line <span className="font-normal text-slate-500">(optional — shows under the question)</span></span>
          <input value={d.hint} onChange={(e) => set({ hint: e.target.value })} placeholder="e.g. 2-pipe brings combustion air in from outside" className={inputCls} />
        </label>
        <label className="inline-flex h-9 items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={d.optional} onChange={(e) => set({ optional: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
          Optional — doesn&apos;t count toward done
        </label>
      </div>

      <Field label="Ask when the job has" help={locked ? undefined : d.when_tags.length ? "Any one of these is enough." : "None picked — every install asks it."}>
        {locked ? (
          <p className="text-sm text-slate-600">{d.when_tags.map(vocab.labelOf).join(", ") || "Every install"}</p>
        ) : (
          <TagPicker value={d.when_tags} onChange={(v) => set({ when_tags: v })} vocab={vocab} label="Ask when the job has" />
        )}
      </Field>

      {!locked && (
        <div className="rounded-xl border border-slate-200 bg-white/70">
          <button type="button" onClick={() => setMore((v) => !v)} aria-expanded={more}
            className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-semibold text-slate-700 hover:text-slate-900">
            <ChevronDown className={cn("h-4 w-4 text-slate-400 transition-transform", more && "rotate-180")} />
            More rules
            <span className="font-normal text-slate-500">— skip for some equipment, wait for another answer, pre-pick from the job</span>
          </button>
          {more && (
            <div className="space-y-5 border-t border-slate-100 px-3 py-3">
              <Field label="But don't ask when the job has" help={d.unless_tags.length ? undefined : "None picked — nothing stops it."}>
                <TagPicker value={d.unless_tags} onChange={(v) => set({ unless_tags: v })} vocab={vocab} label="But don't ask when the job has" />
              </Field>

              <Field label="Only ask after another answer">
                <select value={d.wa_key} onChange={(e) => set({ wa_key: e.target.value, wa_in: [] })} aria-label="Only ask after" className={cn(inputCls, "max-w-md")}>
                  <option value="">No — ask it right away</option>
                  {sections.map((s) => {
                    const inSec = others.filter((x) => x.section_key === s.key);
                    return inSec.length ? (
                      <optgroup key={s.key} label={s.title}>
                        {inSec.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                      </optgroup>
                    ) : null;
                  })}
                </select>
                {waQ && (
                  <div className="mt-2">
                    <div className="mb-1 text-xs text-slate-500">…is one of:</div>
                    <div className="flex flex-wrap gap-1.5">
                      {waQ.options.map((o) => (
                        <Chip key={o} on={d.wa_in.includes(o)} onClick={() => set({ wa_in: d.wa_in.includes(o) ? d.wa_in.filter((x) => x !== o) : [...d.wa_in, o] })}>{o}</Chip>
                      ))}
                    </div>
                  </div>
                )}
              </Field>

              {!d.is_text && (
                <Field label="Pre-pick from what's on the job" info="The pick is filled in (with a sparkle) until someone changes it — a thermostat on the job means New thermostat.">
                  <div className="space-y-1.5">
                    {d.implied.map((i) => (
                      <div key={i.id} className="flex flex-wrap items-center gap-1.5">
                        <span className="text-sm text-slate-600">When the job has</span>
                        <select value={i.src} aria-label="When the job has"
                          onChange={(e) => set({ implied: d.implied.map((x) => (x.id === i.id ? { ...x, src: e.target.value } : x)) })}
                          className={cn(inputCls, "w-auto min-w-[180px]")}>
                          <option value="">Pick one…</option>
                          <optgroup label="Equipment">{vocab.equipment.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</optgroup>
                          <optgroup label="Add-ons & parts">{vocab.addons.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</optgroup>
                        </select>
                        <span className="text-sm text-slate-600">pick</span>
                        <select value={i.choiceId} aria-label="Pick"
                          onChange={(e) => set({ implied: d.implied.map((x) => (x.id === i.id ? { ...x, choiceId: e.target.value } : x)) })}
                          className={cn(inputCls, "w-auto min-w-[160px]")}>
                          <option value="">Pick one…</option>
                          {d.choices.filter((c) => c.text.trim()).map((c) => <option key={c.id} value={c.id}>{c.text.trim()}</option>)}
                        </select>
                        <IconBtn label="Remove this pre-pick" danger onClick={() => set({ implied: d.implied.filter((x) => x.id !== i.id) })}><X className="h-4 w-4" /></IconBtn>
                      </div>
                    ))}
                    <button type="button" onClick={() => set({ implied: [...d.implied, { id: nid(), src: "", choiceId: "" }] })}
                      className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-900">
                      <Plus className="h-4 w-4" /> Add a pre-pick
                    </button>
                  </div>
                </Field>
              )}

              {d.hint.trim() && (
                <Field label="Show the helper line only when the job has" help={d.hint_when.length ? undefined : "None picked — it always shows."}>
                  <TagPicker value={d.hint_when} onChange={(v) => set({ hint_when: v })} vocab={vocab} label="Show the helper line only when the job has" />
                </Field>
              )}
            </div>
          )}
        </div>
      )}

      {err && <p role="alert" className="text-sm font-medium text-rose-600">{err}</p>}
      {renamed.length > 0 && count > 0 && !err && (
        <p className="text-xs text-slate-500">{count} open job{count === 1 ? " has" : "s have"} an answer here — any that picked a renamed choice get the new wording when you save.</p>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
        <Button type="submit" size="sm" loading={save.isPending}><Check className="h-3.5 w-3.5" strokeWidth={3} /> {q ? "Save" : "Add Question"}</Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => onClose()}>Cancel</Button>
      </div>
    </form>
  );
}
